import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, X, SwitchCamera, Flashlight, Copy, RotateCcw, Smartphone, LocateFixed } from 'lucide-react';
import { toFa } from '../../utils/faFormat';
import { distanceToSite } from '../../hooks/useGeoFix';
import { grabFrame, renderEvidence, sha256Hex, cameraSignals, bandLines, inAppBrowser, isIOS } from '../../installer/pwa/camera';
import { isStandalone } from '../../installer/pwa/useInstallPrompt';
import '../../styles/InstallerPages.css';

// The only way an installer evidence photo is made: a frame from a live stream.
// There is deliberately no file input, picker or fallback in any state.

const LENS_KEY = 'installer_camera_lens';
const readLens = () => { try { return localStorage.getItem(LENS_KEY); } catch { return null; } };
const saveLens = (id) => {
  try { if (id) localStorage.setItem(LENS_KEY, id); else localStorage.removeItem(LENS_KEY); } catch { /* storage blocked: default lens */ }
};
// ponytail: a fix older than this at the shutter asks for a fresh one (design 3.5).
const FIX_MAX_AGE_MS = 30000;
const SIZE = { width: { ideal: 1920 }, height: { ideal: 1080 } };
const ERROR_KIND = { NotAllowedError: 'denied', NotFoundError: 'notfound', OverconstrainedError: 'notfound', NotReadableError: 'busy' };

const freshFix = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy, fix_at: p.timestamp }),
    () => resolve(null),
    { enableHighAccuracy: true, maximumAge: FIX_MAX_AGE_MS, timeout: 10000 });
});
const fixAge = (fix, now) => now - new Date(fix.fix_at).getTime();
// The first frame can take a second on a cheap phone; before it, a grab reads a 0x0 or blank video.
const frameReady = (video) => Boolean(video) && video.readyState >= 2 && video.videoWidth > 0;

const DENIED_STEPS = {
  ios: ['«تنظیمات» گوشی را باز کنید و «Safari» را بزنید.', 'در بخش تنظیمات وب‌سایت‌ها، «دوربین» را روی «اجازه» یا «پرسیدن» بگذارید.', 'به این صفحه برگردید و «تلاش دوباره» را بزنید.'],
  android: ['در Chrome روی نماد کنار نشانی صفحه (بالای صفحه) بزنید.', '«مجوزها» یا «تنظیمات سایت» را باز کنید.', '«دوربین» را روی «اجازه» بگذارید.', 'برگردید و «تلاش دوباره» را بزنید.'],
  // The app installed on the home screen has no address bar; its site permission sits in the app info.
  app: ['انگشت را روی نماد برنامه در صفحهٔ اصلی گوشی نگه دارید و «اطلاعات برنامه» را بزنید.', '«مجوزها» یا «تنظیمات سایت» را باز کنید.', '«دوربین» را روی «اجازه» بگذارید.', 'به برنامه برگردید و «تلاش دوباره» را بزنید.'],
};

export default function CameraCapture({ open, onClose, onShot, stamp = true, jobCode, label, getFix, requireFix = false, clockOffsetMs = 0, site, challenge, alert }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const genRef = useRef(0);
  const signalsRef = useRef(null);
  const lastFixRef = useRef(null);
  const busyRef = useRef(false);
  const shotsRef = useRef(0);
  const dialogRef = useRef(null);
  const focusRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting | live | browser | denied | notfound | busy | other
  const [lenses, setLenses] = useState([]);
  const [torch, setTorch] = useState(null); // null: no torch on this lens
  const [busy, setBusy] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [shotError, setShotError] = useState('');
  const [copied, setCopied] = useState(null);

  const stop = useCallback(() => {
    genRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    stop();
    const gen = genRef.current;
    const media = navigator.mediaDevices;
    if (!media?.getUserMedia || inAppBrowser()) { setStatus('browser'); return; }
    setStatus('starting');
    const lens = readLens();
    let stream;
    try {
      stream = await media.getUserMedia({ video: lens ? { deviceId: { exact: lens }, ...SIZE } : { facingMode: { ideal: 'environment' }, ...SIZE }, audio: false });
    } catch (e) {
      if (gen !== genRef.current) return;
      // A remembered lens can vanish (other phone, new permission): forget it, retry the default.
      if (lens && ['OverconstrainedError', 'NotFoundError'].includes(e?.name)) { saveLens(null); start(); return; }
      setStatus(ERROR_KIND[e?.name] || 'other');
      return;
    }
    if (gen !== genRef.current) { stream.getTracks().forEach((track) => track.stop()); return; }
    streamRef.current = stream;
    const track = stream.getVideoTracks()[0];
    signalsRef.current = cameraSignals(track, media);
    setTorch(track?.getCapabilities?.().torch ? false : null);
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      Promise.resolve(videoRef.current.play?.()).catch(() => {});
    }
    if (frameReady(videoRef.current)) setStatus('live');
    Promise.resolve(media.enumerateDevices?.()).then((list) => {
      if (gen === genRef.current && Array.isArray(list)) setLenses(list.filter((d) => d.kind === 'videoinput' && d.deviceId).map((d) => d.deviceId));
    }).catch(() => {});
  }, [stop]);
  // Turns the shutter on once the stream shows its first frame (loadeddata or playing).
  const onFrame = () => { if (streamRef.current && frameReady(videoRef.current)) setStatus((s) => (s === 'starting' ? 'live' : s)); };

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    shotsRef.current = 0; setAnnouncement(''); setShotError(''); setCopied(null);
    start();
    const onVisibility = () => (document.visibilityState === 'hidden' ? stop() : start());
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
      if (opener && opener !== document.body && document.contains(opener)) opener.focus?.();
    };
  }, [open, start, stop]);

  const failed = !['starting', 'live'].includes(status);
  useEffect(() => { if (open) focusRef.current?.focus(); }, [open, failed]);

  if (!open) return null;

  const fix = getFix?.() || lastFixRef.current || null;
  const distance = distanceToSite(fix, site);
  const waitingFix = requireFix && !fix;
  const blocked = busy || status !== 'live' || waitingFix;

  const shoot = async () => {
    if (busyRef.current || blocked || !frameReady(videoRef.current)) return;
    busyRef.current = true; setBusy(true); setShotError('');
    try {
      const shutterAt = Date.now();
      const frame = grabFrame(videoRef.current);
      let shotFix = fix;
      if (getFix && (!shotFix || !(fixAge(shotFix, shutterAt) <= FIX_MAX_AGE_MS))) shotFix = (await freshFix()) || shotFix;
      if (shotFix) lastFixRef.current = shotFix;
      const lines = bandLines({ jobCode, at: shutterAt + clockOffsetMs, accuracy_m: shotFix?.accuracy_m, distance_m: distanceToSite(shotFix, site) });
      const { blob, width, height } = await renderEvidence(frame, { stamp, lines });
      const sha256 = await sha256Hex(blob);
      await onShot({ blob, sha256, width, height, fix: shotFix, camera: signalsRef.current, shutter_at: shutterAt,
        device_captured_at: new Date(shutterAt + clockOffsetMs).toISOString() });
      shotsRef.current += 1;
      setAnnouncement(`عکس ${toFa(shotsRef.current)} گرفته شد.`);
    } catch (e) {
      // onShot names causes another tap cannot fix (a full phone); anything else is worth a retry.
      setShotError(e?.userMessage || 'عکس ذخیره نشد. دوباره دکمهٔ عکس را بزنید.');
    } finally { busyRef.current = false; setBusy(false); }
  };

  const nextLens = () => {
    const current = streamRef.current?.getVideoTracks()[0]?.getSettings?.().deviceId;
    saveLens(lenses[(lenses.indexOf(current) + 1) % lenses.length]);
    start();
  };
  const toggleTorch = async () => {
    try { await streamRef.current?.getVideoTracks()[0]?.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); }
    catch { setTorch(null); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); } catch { setCopied(false); }
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab') return;
    const items = [...dialogRef.current.querySelectorAll('button:not([disabled]), a[href]')];
    if (!items.length) return;
    const [first, last] = [items[0], items[items.length - 1]];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const ios = isIOS();
  const installedApp = status === 'denied' && isStandalone();
  const retry = <button ref={focusRef} type="button" className="inst-btn" onClick={start}><RotateCcw size={18} aria-hidden="true" />تلاش دوباره</button>;
  const problem = {
    browser: <>
      <h3>{ios ? 'این صفحه را در Safari باز کنید' : 'این صفحه را در Chrome باز کنید'}</h3>
      <p>دوربین برنامه در این مرورگر کار نمی‌کند. نشانی صفحه را کپی کنید و در {ios ? 'Safari' : 'Chrome'} باز کنید.</p>
      <bdi className="inst-camera-url" dir="ltr">{window.location.href}</bdi>
      <button ref={focusRef} type="button" className="inst-btn" onClick={copyLink}><Copy size={18} aria-hidden="true" />کپی نشانی صفحه</button>
      {copied !== null && <p role="status">{copied ? `نشانی کپی شد. ${ios ? 'Safari' : 'Chrome'} را باز کنید و نشانی را در نوار بالا بچسبانید.` : 'کپی نشد. نشانی بالا را نگه دارید و کپی کنید.'}</p>}
    </>,
    denied: <>
      <h3>اجازهٔ دوربین داده نشده است</h3>
      <p>بدون دوربین برنامه نمی‌توان عکس کار را ثبت کرد. این مراحل را انجام دهید:</p>
      <ol>{DENIED_STEPS[ios ? 'ios' : installedApp ? 'app' : 'android'].map((step) => <li key={step}>{step}</li>)}</ol>
      {retry}
      {installedApp && !ios && <>
        <p>اگر این گزینه‌ها را پیدا نکردید: نشانی همین صفحه را کپی کنید و در Chrome باز کنید، اجازهٔ دوربین را آنجا بدهید و به برنامه برگردید.</p>
        <button type="button" className="inst-btn" onClick={copyLink}><Copy size={18} aria-hidden="true" />کپی نشانی صفحه</button>
        {copied !== null && <p role="status">{copied ? 'نشانی کپی شد. Chrome را باز کنید و نشانی را در نوار بالا بچسبانید.' : 'کپی نشد. Chrome را باز کنید و نشانی سامانه را بنویسید.'}</p>}
      </>}
    </>,
    notfound: <><h3>دوربین پیدا نشد</h3><p>این گوشی دوربینی در اختیار مرورگر نمی‌گذارد. با گوشی دیگری وارد شوید یا با دفتر تماس بگیرید.</p>{retry}</>,
    busy: <><h3>دوربین در اختیار برنامهٔ دیگری است</h3><p>برنامه‌هایی را که از دوربین استفاده می‌کنند (مثل تماس تصویری) ببندید و دوباره تلاش کنید.</p>{retry}</>,
    other: <><h3>دوربین باز نشد</h3><p>دوباره تلاش کنید. اگر درست نشد، برنامه را کامل ببندید و دوباره باز کنید.</p>{retry}</>,
  }[status];

  return createPortal(
    <div className="inst-camera" role="dialog" aria-modal="true" aria-labelledby="inst-camera-title" ref={dialogRef} onKeyDown={onKeyDown}>
      <header className="inst-camera-top">
        <h2 id="inst-camera-title"><bdi dir="auto">{label || 'دوربین'}</bdi></h2>
        <button type="button" className="inst-camera-close" onClick={onClose} aria-label="بستن دوربین"><X size={20} aria-hidden="true" />بستن</button>
      </header>
      <div className="inst-camera-view" aria-busy={busy}>
        <video ref={videoRef} className="inst-camera-video" hidden={failed} playsInline muted autoPlay aria-hidden="true" onLoadedData={onFrame} onPlaying={onFrame} />
        {failed ? <div className="inst-camera-problem" role="alert">{problem}</div> : <>
          <div className="inst-camera-hud">
            {jobCode && <span className="inst-camera-chip" dir="ltr">JOB {String(jobCode).slice(0, 6)}</span>}
            {getFix && <span className={`inst-camera-chip${waitingFix ? ' is-warning' : ''}`}><LocateFixed size={16} aria-hidden="true" />
              {fix ? `دقت ${toFa(Math.round(fix.accuracy_m))} متر` : 'در حال یافتن موقعیت…'}</span>}
            {getFix && distance != null && <span className="inst-camera-chip">{toFa(Math.round(distance))} متر تا محل مشتری</span>}
            <span className="inst-camera-chip"><Smartphone size={16} aria-hidden="true" />گوشی را عمودی نگه دارید</span>
          </div>
          {status === 'starting' && <p className="inst-camera-wait">در حال روشن کردن دوربین…</p>}
          {challenge && <p className="inst-camera-challenge">{`کد ${challenge.code_fa} (${challenge.code}) را روی کاغذ بنویسید و در کادر بگذارید`}</p>}
        </>}
      </div>
      {!failed && <footer className="inst-camera-controls">
        {shotError && <p className="inst-camera-error" role="alert">{shotError}</p>}
        {/* The page's own notices sit under this full-screen dialog; the page passes them in. */}
        {alert && <p className="inst-camera-error" role="alert">{alert}</p>}
        <p className="inst-camera-status" role="status">
          {busy ? 'در حال ثبت عکس…' : waitingFix ? 'در انتظار موقعیت مکانی؛ دکمهٔ عکس بعد از آن فعال می‌شود.' : announcement}
        </p>
        <div className="inst-camera-slot">{lenses.length > 1 && <button type="button" className="inst-camera-side" onClick={nextLens} disabled={busy}>
          <SwitchCamera size={22} aria-hidden="true" />لنز دیگر</button>}</div>
        <button ref={focusRef} type="button" className="inst-camera-shutter" aria-label="گرفتن عکس" aria-disabled={blocked} onClick={shoot}>
          <Camera size={30} aria-hidden="true" /></button>
        <div className="inst-camera-slot">{torch !== null && <button type="button" className="inst-camera-side" aria-pressed={torch} onClick={toggleTorch} disabled={busy}>
          <Flashlight size={22} aria-hidden="true" />چراغ</button>}</div>
      </footer>}
    </div>,
    document.body,
  );
}
